import React, { FC } from 'react';
import './Table.style.css';
import { useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import Loader from './Loader';
import {
  FactContainer,
  FactTitle,
  SideBar,
  StatusBadge,
  StyledInput,
  SubtitleContainer,
  TableContainer,
  TableWrapper,
  TitleContainer,
} from './Table.style';
import {
  useGetReviewFramesQuery,
  useGetStudyLabelingJobsQuery,
} from '../../../../redux/slices/icardio';
import { IAnnotationSet, IStudyLabelingJob } from 'models/icardio';
import { useReviewMode } from 'services/providers';

interface IProps {
  annotationSet?: IAnnotationSet;
  annotationSetsResponseIsLoading: any;
}

const Table: FC<IProps> = props => {
  const { annotationSet, annotationSetsResponseIsLoading } = props;
  const annotationSetId = annotationSet?.uuid;
  const { isReviewMode } = useReviewMode();

  const [search, setSearch] = useState<string | null>(null);

  const columns = useMemo(() => {
    const getStudyLink = row => `/${row?.studyIcid}/${row?.dicomIcid}`;

    const baseColumns = [
      {
        name: 'ICID',
        selector: row => row.studyIcid,
        sortable: true,
        cell: row => (
          <Link to={getStudyLink(row)} className="data-link">
            {row.studyIcid}
          </Link>
        ),
      },
      {
        name: 'Number of Dicoms',
        selector: row => row.numberOfDicomLabelingJobs,
        sortable: true,
        cell: row => (
          <Link to={getStudyLink(row)} className="data-link">
            {row.numberOfDicomLabelingJobs}
          </Link>
        ),
      },
    ];

    // Conditionally add Procedure type column
    if (annotationSet?.name?.includes('ICE')) {
      baseColumns.push({
        name: 'Procedure type',
        selector: row => row?.procedureType,
        sortable: true,
        cell: row => (
          <Link to={getStudyLink(row)} className="data-link">
            {row.studyProcedureType}
          </Link>
        ),
      });
    }

    baseColumns.push(
      {
        name: 'Number of Labeled Dicoms',
        selector: row => row.numberOfCompletedDicomLabelingJobs,
        sortable: true,
        cell: row => (
          <Link to={getStudyLink(row)} className="data-link">
            {row.numberOfCompletedDicomLabelingJobs}
          </Link>
        ),
      },
      {
        name: 'Status',
        selector: row => row.status,
        sortable: true,
        cell: row => (
          <Link to={getStudyLink(row)} className="data-link">
            <StatusBadge status={row?.status}>{row?.status}</StatusBadge>
          </Link>
        ),
      }
    );

    return baseColumns;
  }, [annotationSetId]);

  const { data: studyLabelingJobs, isLoading: isLoadingStudyLabelingJobs } =
    useGetStudyLabelingJobsQuery(
      {
        annotationSetId,
        paginate: false,
      },
      { skip: !annotationSetId }
    );
  const { data: reviewFramesRequiringAction, isLoading: isLoadingReviewFrames } =
    useGetReviewFramesQuery(
      {
        annotationSetId: annotationSetId as string,
        status: 'IN_REVIEW',
        paginate: false,
      },
      {
        skip: !annotationSetId || !isReviewMode,
      }
    );

  const rejectedStudyIcids = useMemo(
    () =>
      new Set(
        isReviewMode
          ? (reviewFramesRequiringAction ?? []).map(reviewFrame => reviewFrame.studyIcid)
          : []
      ),
    [isReviewMode, reviewFramesRequiringAction]
  );
  const conditionalRowStyles = useMemo(
    () => [
      {
        when: (row: IStudyLabelingJob) => rejectedStudyIcids.has(row.studyIcid),
        classNames: ['rejected-review-row'],
      },
    ],
    [rejectedStudyIcids]
  );

  const isLoading =
    !annotationSet ||
    annotationSetsResponseIsLoading ||
    isLoadingStudyLabelingJobs ||
    (isReviewMode && isLoadingReviewFrames);
  const reviewModeStudyLabelingJobs = useMemo(() => {
    const jobs = Array.isArray(studyLabelingJobs) ? studyLabelingJobs : [];
    if (!isReviewMode) return jobs;
    return jobs.filter(row => rejectedStudyIcids.has(row.studyIcid));
  }, [isReviewMode, rejectedStudyIcids, studyLabelingJobs]);
  const count = useMemo(() => reviewModeStudyLabelingJobs.length, [reviewModeStudyLabelingJobs]);
  const results = useMemo(() => {
    if (!search) return reviewModeStudyLabelingJobs;
    return reviewModeStudyLabelingJobs.filter(row =>
      row?.studyIcid?.toLowerCase()?.includes(search?.toLowerCase())
    );
  }, [reviewModeStudyLabelingJobs, search]);

  const handleSearchOnChange = e => setSearch(e.target.value);

  return (
    <TableContainer>
      <SideBar>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <TitleContainer>{annotationSet?.name}</TitleContainer>
            <SubtitleContainer>
              <FactTitle>Collection Summary</FactTitle>
              <FactContainer>
                <div>Total Studies</div>
                <div>{annotationSet?.totalNumberOfStudyLabelingJobs}</div>
              </FactContainer>
              <FactContainer>
                <div>Labeled Studies</div>
                <div>{annotationSet?.numberOfFinishedStudyLabelingJobs}</div>
              </FactContainer>
              <FactContainer>
                <div>Remaining Studies</div>
                <div>
                  {annotationSet?.totalNumberOfStudyLabelingJobs -
                    annotationSet?.numberOfFinishedStudyLabelingJobs}
                </div>
              </FactContainer>
              <br></br>
              <FactContainer>
                <div>Total Dicoms</div>
                <div>{annotationSet?.totalNumberOfDicomLabelingJobs}</div>
              </FactContainer>
              <FactContainer>
                <div>Labeled Dicoms</div>
                <div>{annotationSet?.numberOfFinishedDicomLabelingJobs}</div>
              </FactContainer>
              <FactContainer>
                <div>Remaining Dicoms</div>
                <div>
                  {annotationSet?.totalNumberOfDicomLabelingJobs -
                    annotationSet?.numberOfFinishedDicomLabelingJobs}
                </div>
              </FactContainer>
            </SubtitleContainer>
          </>
        )}
      </SideBar>
      <TableWrapper>
        <DataTable
          columns={columns}
          data={results as IStudyLabelingJob[]}
          conditionalRowStyles={conditionalRowStyles}
          pagination
          paginationTotalRows={count}
          paginationDefaultPage={1}
          progressPending={isLoading}
          progressComponent={<Loader />}
          persistTableHead
        />
        {!isLoading && (
          <StyledInput
            placeholder="Search..."
            value={search || ''}
            onChange={handleSearchOnChange}
          />
        )}
      </TableWrapper>
    </TableContainer>
  );
};

export default Table;
